import { cloneElement, useEffect, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { NumberTag } from '../crop-circle/Components/Tag/NumberTag';
import { Icon } from '../crop-circle/icons';
import { colors as CropCircleColors, tokens } from '../crop-circle/tokens';

export interface FilterHeadersProps {
  onSearchClose?: () => void;
  onFilterPress: () => void;
  currentSearchKeyword?: string;
  searchComponent?: React.ReactNode;
  filterCount?: number;
  Routes: Record<string, string>;
  showFilterButton?: boolean;
}

export const FilterHeaders = ({
  onFilterPress,
  onSearchClose,
  currentSearchKeyword,
  searchComponent,
  filterCount,
  showFilterButton = true,
}: FilterHeadersProps) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(
    !!currentSearchKeyword
  );

  // Update isSearchExpanded when currentSearchKeyword changes
  useEffect(() => {
    if (currentSearchKeyword) {
      setIsSearchExpanded(true);
    }
  }, [currentSearchKeyword]);

  const handleSearchPress = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);

    if (onSearchClose) {
      onSearchClose();
    }
  };

  const FilterButton = (
    <Pressable onPress={onFilterPress} style={styles.iconButton}>
      <View style={styles.filterIconContainer}>
        <Icon name="Filter" size={24} color={Colors.black} />
        {filterCount !== undefined && filterCount > 0 && (
          <View style={styles.badgeContainer}>
            <NumberTag number={filterCount} variant="black" />
          </View>
        )}
      </View>
    </Pressable>
  );

  return (
    <View
      style={[
        styles.iconButtonsContainer,
        isSearchExpanded && styles.searchExpandedContainer,
      ]}
    >
      {isSearchExpanded ? (
        <>
          <View style={styles.searchInputOuterContainer}>
            <View style={styles.searchInputInnerContainer}>
              {searchComponent
                ? cloneElement(
                    searchComponent as React.ReactElement<{
                      onBlur?: () => void;
                      autoFocus?: boolean;
                    }>,
                    {
                      onBlur: handleSearchClose,
                      autoFocus: !currentSearchKeyword,
                    }
                  )
                : null}
            </View>
            <Pressable
              onPress={handleSearchClose}
              style={styles.closeButton}
              accessibilityRole="button"
              accessibilityLabel="Close search"
            >
              <Icon name="x" size={16} color={Colors.black} />
            </Pressable>
          </View>
          {showFilterButton && FilterButton}
        </>
      ) : (
        <>
          <Pressable onPress={handleSearchPress} style={styles.iconButton}>
            <Icon name="Search" size={24} color={Colors.black} />
          </Pressable>
          {showFilterButton && FilterButton}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  iconButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
    ...Platform.select({
      web: {
        columnGap: tokens.spacing.lg,
      },
    }),
  },
  searchExpandedContainer: {
    ...Platform.select({
      web: {
        flex: 1,
      },
      default: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacing.lg,
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: tokens.colors.brand.white,
        paddingHorizontal: tokens.spacing.lg,
      },
    }),
  },
  searchInputOuterContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CropCircleColors.neutral[200],
    borderRadius: tokens.borderRadius.lg,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm,
    gap: tokens.spacing.sm,
    ...Platform.select({
      web: {
        columnGap: tokens.spacing.sm,
        height: 40,
      },
    }),
  },
  searchInputInnerContainer: {
    flex: 1,
  },
  closeButton: {
    padding: tokens.spacing.xs,
    ...Platform.select({
      web: {
        padding: tokens.spacing.sm,
      },
    }),
  },
  iconButton: {
    padding: tokens.spacing.xs,
    ...Platform.select({
      web: {
        padding: tokens.spacing.sm,
      },
    }),
  },
  filterIconContainer: {
    position: 'relative',
  },
  badgeContainer: {
    position: 'absolute',
    top: -6,
    right: -6,
  },
});
